import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dumbbell, MessageCircle, Zap, ArrowLeft, Loader2, Mail, Lock, User as UserIcon, Building2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import { endpoints } from "../lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const BACKDROPS = [
  { src: "https://images.unsplash.com/photo-1680022702604-292f21514497?auto=format&fit=crop&w=1600&q=80", label: "MMA fighter" },
  { src: "https://images.unsplash.com/photo-1519505907962-0a6cb0167c73?auto=format&fit=crop&w=1600&q=80", label: "CrossFit athlete" },
  { src: "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?auto=format&fit=crop&w=1600&q=80", label: "Strength training" },
];
const ROTATE_MS = 6000;

export default function Login() {
  const navigate = useNavigate();
  const [bIdx, setBIdx] = useState(0);
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [gymName, setGymName] = useState("");

  useEffect(() => {
    const t = setInterval(() => setBIdx(i => (i + 1) % BACKDROPS.length), ROTATE_MS);
    BACKDROPS.forEach(b => { const img = new Image(); img.src = b.src; });
    return () => clearInterval(t);
  }, []);

  const handleEmailPassword = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!email.trim() || !password) return toast.error("Email and password are required");
    if (mode === "signup" && (!fullName.trim() || !gymName.trim())) {
      return toast.error("Full name and gym name are required");
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: { full_name: fullName.trim(), gym_name: gymName.trim() },
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        });
        if (error) throw error;
        // If email confirmation is required, no session yet.
        if (!data?.session) {
          toast.success("Check your email to confirm your account.");
          setBusy(false);
          return;
        }
        // Session is live - bootstrap Mongo profile then go to dashboard.
        await endpoints.signupComplete({ gym_name: gymName.trim(), full_name: fullName.trim() });
        window.location.replace("/dashboard");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) throw error;
        window.location.replace("/dashboard");
      }
    } catch (err) {
      const msg = err?.message || "Could not authenticate";
      toast.error(msg);
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL - use window.location.origin
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { access_type: "offline", prompt: "select_account" },
        },
      });
      if (error) throw error;
      // Browser will navigate away to Google. Keep busy=true so the button shows the spinner.
    } catch (err) {
      toast.error(err?.message || "Google sign-in failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <Link
          to="/"
          data-testid="login-back-home"
          className="absolute top-6 left-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center mb-8 w-fit">
            <img
              src={"/logo-light.png"}
              alt="Fitnudge"
              data-testid="login-logo"
              className="h-7 w-auto dark:hidden"
              draggable={false}
            />
            <img
              src={"/logo-dark.png"}
              alt="Fitnudge"
              className="h-7 w-auto hidden dark:block"
              draggable={false}
            />
          </Link>

          <div className="bg-card border border-border rounded-2xl p-7 shadow-sm">
            <h1
              data-testid="login-headline"
              className="font-display text-2xl font-bold tracking-tight text-foreground mb-1.5"
            >
              {mode === "signup" ? "Create your gym" : "Welcome back"}
            </h1>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed" data-testid="login-subtext">
              {mode === "signup"
                ? "Run your gym. Not spreadsheets."
                : "Sign in to manage your gym in one place."}
            </p>

            {/* Tab switcher */}
            <div className="flex gap-1 mb-5 p-1 bg-muted rounded-lg" role="tablist">
              <button
                type="button"
                onClick={() => setMode("signin")}
                data-testid="tab-signin"
                role="tab"
                aria-selected={mode === "signin"}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  mode === "signin" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => setMode("signup")}
                data-testid="tab-signup"
                role="tab"
                aria-selected={mode === "signup"}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  mode === "signup" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
                }`}
              >
                Sign up
              </button>
            </div>

            <form onSubmit={handleEmailPassword} className="space-y-3">
              {mode === "signup" && (
                <>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/70" />
                    <input
                      type="text"
                      value={gymName}
                      onChange={(e) => setGymName(e.target.value)}
                      placeholder="Gym name"
                      data-testid="signup-gym-name"
                      autoComplete="organization"
                      required
                      className="w-full pl-9 pr-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                    />
                  </div>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/70" />
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your full name"
                      data-testid="signup-full-name"
                      autoComplete="name"
                      required
                      className="w-full pl-9 pr-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                    />
                  </div>
                </>
              )}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/70" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  data-testid="auth-email"
                  autoComplete="email"
                  required
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/70" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  data-testid="auth-password"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  required
                  minLength={6}
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid={mode === "signup" ? "signup-submit-btn" : "signin-submit-btn"}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:opacity-90 font-medium py-2.5 rounded-lg transition-opacity disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {busy ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground/70">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              data-testid="continue-with-google-btn"
              className="w-full inline-flex items-center justify-center gap-3 bg-foreground text-background hover:opacity-90 font-medium py-2.5 rounded-lg transition-opacity disabled:opacity-60"
            >
              <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.4 6.4 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16.2 18.9 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34.4 6.4 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.5 35 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.3 5.3c-.4.4 6.4-4.7 6.4-14.2 0-1.3-.1-2.6-.4-4z"/>
              </svg>
              Continue with Google
            </button>

            <p className="mt-5 text-xs text-muted-foreground/80 text-center leading-relaxed">
              {mode === "signup" ? (
                <>
                  Already have an account?{" "}
                  <button onClick={() => setMode("signin")} className="text-primary font-semibold hover:underline" data-testid="switch-to-signin">
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  New here?{" "}
                  <button onClick={() => setMode("signup")} className="text-primary font-semibold hover:underline" data-testid="switch-to-signup">
                    Sign up
                  </button>
                </>
              )}
            </p>
          </div>

          <div className="mt-8 space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <MessageCircle className="w-4 h-4 mt-0.5 text-[#25D366]" />
              <span>WhatsApp click-to-chat on every lead & member</span>
            </div>
            <div className="flex items-start gap-3">
              <Zap className="w-4 h-4 mt-0.5 text-amber-500" />
              <span>Auto-generated "Today's Tasks" - no more guessing</span>
            </div>
            <div className="flex items-start gap-3">
              <Dumbbell className="w-4 h-4 mt-0.5 text-foreground" />
              <span>Kanban pipeline, trial tracking, renewal reminders</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: rotating gym imagery */}
      <div className="hidden lg:block lg:w-[45%] relative overflow-hidden bg-foreground">
        {BACKDROPS.map((b, i) => (
          <div
            key={b.src}
            data-testid={`login-backdrop-${i}`}
            aria-hidden={i !== bIdx}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-out ${
              i === bIdx ? "opacity-100" : "opacity-0"
            }`}
            style={{ backgroundImage: `url('${b.src}')` }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />

        <div className="absolute bottom-10 left-10 right-10">
          <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-6">
            <p className="font-display text-xl md:text-2xl font-semibold text-white leading-snug">
              "Stop managing spreadsheets. Start closing memberships."
            </p>
            <p className="text-sm text-white/70 mt-3">Built for combat sports & fitness gyms in India.</p>
          </div>
        </div>

        <div className="absolute top-6 right-6 flex gap-1.5" data-testid="login-backdrop-dots">
          {BACKDROPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === bIdx ? "w-6 bg-white" : "w-1.5 bg-white/40"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
